// Typed access to the Quran dataset (5 essential surahs, 24 translation languages)
import raw from '@/data/quran.json';

export interface Ayah { n: number; g: number; text: string }
export interface EditionInfo { id: string; name: string; native: string; flag: string; rtl?: boolean }
export interface SurahMeta { nameAr: string; nameEn: string; nameFr: string; meaningEn: string; meaningFr: string; revelation: string; ayahs: number }

interface QuranData {
  meta: {
    title: string;
    generatedAt: string;
    languagesCount: number;
    audioBase: string;
    editions: EditionInfo[];
    surahs: Record<string, SurahMeta>;
  };
  arabic: Record<string, Ayah[]>;
  transliteration: Record<string, Ayah[]>;
  translations: Record<string, Record<string, Ayah[]>>;
}

const data = raw as QuranData;

// Curated order — the 24 validated languages first (user requirement: 24 languages, five continents)
const PRIORITY = ['fr.hamidullah', 'en.sahih', 'es.cortes', 'de.aburida', 'ru.kuliev', 'tr.diyanet', 'ur.jalandhry', 'id.indonesian', 'hi.farooq', 'bn.bengali', 'zh.jian', 'pt.elhayek', 'it.piccardo', 'nl.keyzer', 'sw.barwani', 'fa.ansarian', 'ha.gumi', 'so.abduh', 'ms.basmeih', 'ja.japanese', 'ko.korean', 'th.thai', 'am.sadiq', 'ml.abdulhameed'];

const VALIDATED_24 = PRIORITY.filter(id => data.translations[id]).slice(0, 24);

export const EDITIONS: EditionInfo[] = VALIDATED_24
  .map(id => data.meta.editions.find(e => e.id === id)!)
  .filter(Boolean);

export const LANGS_COUNT = EDITIONS.length;
export const AUDIO_BASE = data.meta.audioBase;
export const SURAH_IDS = [1, 108, 112, 113, 114];
export const SURAH_META = data.meta.surahs;

export function getSurahMeta(surah: number): SurahMeta {
  return SURAH_META[String(surah)];
}

export function getArabic(surah: number): Ayah[] {
  return (data.arabic[String(surah)] || []).map(a => ({ ...a, text: a.text.replace(/^\uFEFF/, '') }));
}

export function getTransliteration(surah: number): Ayah[] {
  return data.transliteration[String(surah)] || [];
}

export function getTranslation(surah: number, edition: string): Ayah[] {
  return data.translations[edition]?.[String(surah)] || [];
}

export function getAudioUrl(reciter: string, globalAyah: number): string {
  return `${AUDIO_BASE}/${reciter}/${globalAyah}.mp3`;
}

export const RECITERS = [
  { id: 'ar.alafasy', key: 'alafasy' },
  { id: 'ar.husary', key: 'husary' },
  { id: 'ar.abdurrahmaansudais', key: 'sudais' },
  { id: 'ar.minshawimujawwad', key: 'minshawi' },
] as const;

export function searchTranslations(query: string, edition: string): { surah: number; ayah: number; text: string; arabic: string }[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const results: { surah: number; ayah: number; text: string; arabic: string }[] = [];
  for (const s of SURAH_IDS) {
    const tr = getTranslation(s, edition);
    const ar = getArabic(s);
    for (const a of tr) {
      if (a.text.toLowerCase().includes(q)) {
        results.push({ surah: s, ayah: a.n, text: a.text, arabic: ar.find(x => x.n === a.n)?.text || '' });
      }
    }
  }
  return results;
}
